import { getBookingState, getClientData, getCurrentStageId, getStages, getUnitData } from './state.js';
import { hasUsableCoordinates } from './utils/coordinates.js';

const stageVisibilityRules = {
    extras: hasExtrasToDisplay
};

const stageAutoAdvanceRules = {
    extras: () => !hasExtrasToDisplay(),
    sensible_weather: () => !hasSensibleWeatherLocation()
};

function hasSensibleWeatherLocation() {
    const bookingState = getBookingState();
    const unit = getUnitData(bookingState.unit);

    return !!(unit && hasUsableCoordinates(unit.latitude, unit.longitude));
}

function hasExtrasToDisplay() {
    const extrasSection = document.querySelector("#atb-extras-section");
    if (!extrasSection)
        return false;

    return extrasSection.querySelector(".atb-addon:not(.atb-addon-heading):not(.d-none)") !== null;
}

function hasStageVisibilityRule(stage) {
    return typeof stageVisibilityRules[stage.id] === "function";
}

function shouldAutoAdvanceStage(stageId) {
    const rule = stageAutoAdvanceRules[stageId];

    return typeof rule === "function" && rule();
}

function getStageCollapseElement(stageId) {
    return document.querySelector(`#collapse-${stageId}`);
}

function isStageOpen(stageId) {
    return getStageCollapseElement(stageId)?.classList.contains("show") === true;
}

function shouldApplyStageVisibilityRules() {
    return getClientData().mode === "preset";
}

function isStageVisible(stage) {
    const rule = stageVisibilityRules[stage.id];

    return typeof rule !== "function" || !shouldApplyStageVisibilityRules() || rule(stage);
}

function getVisibleStages() {
    return getStages().filter(isStageVisible);
}

function syncStageVisibility() {
    const stages = getStages();

    stages.forEach(stage => {
        const shouldShowStage = isStageVisible(stage);
        const stageEl = document.querySelector(`#atb-checkout-accordion-item-${stage.id}`);
        const progressEl = document.querySelector(`.progress-bar[data-stage-id="${stage.id}"]`)?.closest(".progress");

        if (hasStageVisibilityRule(stage)) {
            stageEl?.classList.toggle("d-none", !shouldShowStage);
            progressEl?.classList.toggle("d-none", !shouldShowStage);
        }
    });

    syncVisibleStagePresentation();
}

function syncVisibleStagePresentation() {
    const visibleStages = getVisibleStages();

    syncVisibleStageNumbers(visibleStages);
    syncMobileProgressBarPresentation(visibleStages);
}

function syncVisibleStageNumbers(visibleStages) {
    visibleStages.forEach((stage, index) => {
        const stageEl = document.querySelector(`#atb-checkout-accordion-item-${stage.id}`);
        const stageNumberEl = stageEl?.querySelector(".atb-stage-number-value");

        if (stageNumberEl)
            stageNumberEl.textContent = index + 1;
    });
}

function syncMobileProgressBarPresentation(visibleStages) {
    const progressBar = document.querySelector("#atb-mobile-progress-bar");
    if (!progressBar)
        return;

    const currentStageId = getCurrentStageId();
    const visibleStageIds = visibleStages.map(stage => stage.id);
    const activeStageIndex = Math.max(0, visibleStageIds.indexOf(currentStageId));
    const visibleStageWidth = visibleStages.length > 0 ? 100 / visibleStages.length : 100;

    getStages().forEach(stage => {
        const progressBarEl = progressBar.querySelector(`.progress-bar[data-stage-id="${stage.id}"]`);
        const progressWrapperEl = progressBarEl?.closest(".progress");
        const visibleStageIndex = visibleStageIds.indexOf(stage.id);
        const isVisible = visibleStageIndex !== -1;

        progressWrapperEl?.classList.toggle("d-none", !isVisible);

        if (progressWrapperEl && isVisible)
            progressWrapperEl.style.width = `${visibleStageWidth}%`;

        if (!progressBarEl)
            return;

        progressBarEl.classList.toggle("atb-progress-complete", isVisible && visibleStageIndex < activeStageIndex);
        progressBarEl.classList.toggle("atb-progress-active", isVisible && visibleStageIndex === activeStageIndex);
        progressBarEl.classList.toggle("atb-progress-pending", isVisible && visibleStageIndex > activeStageIndex);
    });

    progressBar.setAttribute("aria-valuenow", `Stage ${activeStageIndex + 1} active`);
}

function scheduleStageVisibilitySync() {
    const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));

    schedule(syncStageVisibility);
}

function initStageVisibility() {
    document.addEventListener("CheckoutUnitChanged", scheduleStageVisibilitySync);
}

export {
    getStageCollapseElement,
    getVisibleStages,
    initStageVisibility,
    isStageOpen,
    shouldAutoAdvanceStage,
    syncStageVisibility
};
